import React, { act, useEffect, useMemo, useState } from 'react'
import { Map, MapPin, Share } from 'lucide-react'
import { TailSpin } from 'react-loader-spinner'
import { useSelector } from 'react-redux'
import { AppState } from '../../state'
import useCopy from '../../config/useCopy'
import { DDGColors } from '../../constants/colors'
import {
	useSwitchPage,
	useWellnessClassById,
} from '../../state/features/applicationSlice/hooks'
import { formatAddress, getTimestampDate } from '../../functions'
import Image from '../../components/Image'
import ReturnToFacilityLink from '../../components/ReturnToFacilityLink'
import { WellnessClass, WellnessClassSubscription } from '../../types/HealthInstitution'
import { shareOnMobile } from 'react-mobile-share'
import { ApplicationPages } from '../../state/features/applicationSlice'

interface SuccessProps {}

const BookingClassSuccess: React.FC<SuccessProps> = ({}) => {
	const [booking, setBooking] = useState<WellnessClassSubscription>()

	const { activeFacility, institutionClasses  } = useSelector<AppState, AppState['application']>(
		(state) => state.application
	)
	const urlParams = new URLSearchParams(window.location.search)
	const bookingId = urlParams.get('id')
	const { handleCopy } = useCopy()
	const router = useSwitchPage()
	const activeClass = useMemo(()=>{
		return institutionClasses.find(wellnessClass=>wellnessClass.id === booking?.classId) ?? null
	},[booking])
	console.log('activeClass', activeClass)
	const getWellnessClassBooking = useWellnessClassById()
	useEffect(() => {
		if (bookingId) {
			fetchBookingData(bookingId)
		}
	}, [bookingId])
	const fetchBookingData = async (id: string) => {
		const bookingData = await getWellnessClassBooking(id)
		if (bookingData) {
			setBooking(bookingData)
		}
	}
	if (!bookingId) {
		router(ApplicationPages.HOME)
	}
	if (!activeFacility || !booking || !activeClass) {
		return (
			<div className='flex items-center justify-center w-full h-screen'>
				<TailSpin width={20} height={20} color={DDGColors.primary} />
			</div>
		)
	}
	return (
		<div className='flex flex-col min-h-screen text-gray-800 bg-white'>
			{/* Hero banner */}
			<div className='relative h-40'>
				<div className='absolute z-10 flex flex-row items-center justify-end w-full px-4 top-4'>
					<button
						onClick={() => {
							shareOnMobile(
								{
									text: `I just found my new wellness sanctuary at ${activeFacility.details.institutionName}. Rejuvenate your mind and body—you deserve this! Check it out`,
									url: window.location.origin + window.location.pathname,
									title: `${activeFacility.details.institutionName} - DrDoGood`,
								},
								(message) => {
									console.log(
										'message sharing',
										window.location.origin + window.location.pathname
									)
									handleCopy(window.location.origin + window.location.pathname)
								}
							)
						}}
						className='flex items-center justify-center w-8 h-8 bg-opacity-50 rounded-full bg-slate-700'
					>
						<Share size={20} color='white' />
					</button>
				</div>
				<div className='h-40'><Image
					className={`h-40 w-full ${activeClass.image ? 'object-cover': 'object-contain '} bg-center bg-no-repeat bg-[#257C6C]`}
					src={activeClass.image ?? '/img/succes-header.png'}
					alt={'header'}
					useBase={!activeClass.image}
				/></div>
				
				<div className='absolute px-6 text-white bottom-5'>
					<h1 className='mb-1 text-2xl font-bold'>Congratulations</h1>
					<p className='text-xs font-light'>
						Your Session was booked successfully !
					</p>
				</div>
			</div>

			{/* Content */}
			<div className='flex flex-col flex-1 px-3 pb-6'>
				{/* Spa info */}
				<div className='flex flex-row items-center py-4 '>
					<div className='relative flex flex-row items-center justify-center flex-shrink-0 w-10 h-10 p-1 mr-2 text-sm text-white rounded-full cursor-pointer bg-gainsboro'>
						<Image
							useBase={false}
							className='flex-1 w-full h-full bg-white rounded-full p-dark border-primaryLight'
							src={activeFacility.details.logoImage ?? ''}
							alt={activeFacility.details.institutionName}
						/>
					</div>
					<div className='flex-1'>
						<p className='text-sm font-bold text-left capitalize'>
							{activeFacility.details.institutionName}
						</p>
						<div className='flex flex-row items-center space-x-1'>
							<MapPin size={12} color={'#667085'} />
							<p className='text-[10px] text-[#667085]'>
								{activeFacility?.details?.address?.city ?? ''},{' '}
								{activeFacility?.details?.address?.country ?? ''}
							</p>
						</div>
					</div>
					<div className='flex flex-row items-center justify-center p-2.5 rounded-full bg-primary'>
						<Map
							fill={DDGColors.primaryLight}
							size={14}
							color={DDGColors.primary}
						/>
					</div>
				</div>
				<div className='pb-2'>
					<p className='text-xs text-gray-500'>{activeClass.bookingDetails.message}</p>
					{activeClass.bookingDetails.note && <p className='pt-2 text-xs text-blood'>{activeClass.bookingDetails.note}</p>}
				</div>
				<hr className='border-[#EEEEEE]' />

				{/* Booking details */}
				<h3 className='my-2 text-lg font-semibold'>Booking Details</h3>

				<div className='grid grid-cols-12 pb-4 gap-y-2'>
					<div className='col-span-4 text-sm font-medium text-gray-700'>
						Date and Time
					</div>
					<div className='col-span-8 text-xs text-right text-gray-500'>
						{new Date(
							new Date(getTimestampDate(booking?.startTime)) ?? 0
						).toUTCString()}
					</div>

					<div className='col-span-4 text-sm font-medium text-gray-700'>
						Location
					</div>
					<div className='col-span-8 text-xs text-right text-gray-500'>
						{formatAddress(activeFacility?.details.address)}
					</div>
				</div>
				<hr className='border-[#EEEEEE]' />

				{/* Instructors and Amount */}
				<div className='flex justify-between py-4'>
					<div>
						<p className='font-semibold text-md'>Instructors</p>
						<p className='text-xs text-gray-500 '>Awo Yoga</p>
					</div>
					<div>
						<p className='font-semibold text-md'>Booking Status</p>
						<p className='text-xs text-right text-gray-500'>{booking.status}</p>
					</div>
				</div>

				<hr className='border-[#EEEEEE] ' />

				{/* QR Code */}
				<div className='flex items-center justify-between'>
					<div className='flex-1'>
						<h4 className='mb-1 font-semibold text-gray-800 text-md'>
							Access QR Code
						</h4>
						<p className='text-xs leading-tight text-gray-600'>
							This QR Code will be required
							<br />
							to start your session.
						</p>
					</div>
					<div className='flex items-center justify-center bg-white w-44 h-44'>
						<div className='w-40 h-40 p-1 '>
							<Image src={booking.qrDataUrl} alt='' useBase={false} />
						</div>
					</div>
				</div>

				<hr className='border-gray-200 ' />

				{/* Experience Tips */}
				<h4 className='pt-4 font-semibold text-gray-800'>Tips:</h4>
				<div className='space-y-3 '>
					{/* {[...(activeClass.bookingDetails.tips ?? [])].map((tip)=>{
						return <p className='text-sm text-gray-700'>
						- {tip}
					</p>
					})} */}
					<p className='text-sm text-gray-700'>
						- {activeClass.bookingDetails.tips}
					</p>
				</div>

				{/* App management */}
				<div className='flex flex-col justify-center flex-1 mt-6 text-center'>
					<p className='mb-1 font-semibold text-gray-800'>
						Manage your booking in the app
					</p>
					<p className='mb-4 text-xs text-gray-600'>
						Available on iOS and Android.
					</p>

					<div className='relative flex flex-row items-center justify-center w-full my-3 space-x-4 '>
						<a
							target='_blank'
							href='https://apps.apple.com/gh/app/drdogood/id6450893844'
							rel='noreferrer'
							className=''
						>
							<div className='relative h-8'>
								<Image
									useBase
									src='/icons/apple.svg'
									loading='lazy'
									alt='Apple AppStore'
									className='h-full'
								/>
							</div>
						</a>
						<a
							target='_blank'
							href='https://play.google.com/store/apps/details?id=app.drdogood'
							rel='noreferrer'
						>
							<div className='relative h-8'>
								<Image
									useBase
									src='/icons/google-play.svg'
									loading='lazy'
									alt='Google Play Store'
									className='h-full'
								/>
							</div>
						</a>
					</div>
					<ReturnToFacilityLink activeFacility={activeFacility} />
				</div>
			</div>
		</div>
	)
}
export default BookingClassSuccess
